import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  MapPin, 
  Phone,
  Building2,
  Search,
  ToggleLeft,
  ToggleRight,
  AlertTriangle
} from 'lucide-react';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/layout/AdminLayout';
import Modal from '../../components/ui/Modal';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { stationsAPI } from '../../services/api';

const StationsManagement = () => {
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingStation, setEditingStation] = useState(null);
  const [deletingStation, setDeletingStation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    city: '',
    state: '',
    contact: ''
  });

  useEffect(() => {
    fetchStations();
  }, []);

  const fetchStations = async () => {
    try {
      const response = await stationsAPI.getAll({ active: 'all' });
      setStations(response.data.stations);
    } catch (error) {
      console.error('Error fetching stations:', error);
      toast.error('Failed to load stations');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (station = null) => {
    if (station) {
      setEditingStation(station);
      setFormData({
        name: station.name,
        code: station.code,
        city: station.city,
        state: station.state,
        contact: station.contact || ''
      });
    } else {
      setEditingStation(null);
      setFormData({
        name: '',
        code: '',
        city: '',
        state: '',
        contact: ''
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingStation(null);
  };

  const handleOpenDeleteModal = (station) => {
    setDeletingStation(station);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeletingStation(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingStation) {
        await stationsAPI.update(editingStation._id, formData);
        toast.success('Station updated successfully');
      } else {
        await stationsAPI.create(formData);
        toast.success('Station created successfully');
      }
      
      handleCloseModal();
      fetchStations();
    } catch (error) {
      console.error('Error saving station:', error);
      toast.error(error.response?.data?.error || 'Failed to save station');
    }
  };

  const handleToggleActive = async (station) => {
    try {
      await stationsAPI.update(station._id, { isActive: !station.isActive });
      toast.success(`Station ${station.isActive ? 'deactivated' : 'activated'}`);
      fetchStations();
    } catch (error) {
      console.error('Error toggling station:', error);
      toast.error('Failed to update station');
    }
  };

  const handleDelete = async () => {
    if (!deletingStation) return;

    try {
      await stationsAPI.delete(deletingStation._id);
      toast.success('Station deleted successfully');
      handleCloseDeleteModal();
      fetchStations();
    } catch (error) {
      console.error('Error deleting station:', error);
      toast.error(error.response?.data?.error || 'Failed to delete station');
    }
  };

  const filteredStations = stations.filter(
    (station) =>
      station.name.toLowerCase().includes(search.toLowerCase()) ||
      station.code.toLowerCase().includes(search.toLowerCase()) ||
      station.city.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-midnight-900">
              Stations Management
            </h1>
            <p className="text-midnight-600 mt-1">
              Manage stations in the system
            </p>
          </div>
          <button 
            onClick={() => handleOpenModal()}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add Station
          </button>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-midnight-400" />
          <input
            type="text"
            placeholder="Search stations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-field pl-12"
          />
        </div>

        {/* Stations Table */}
        <div className="card overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <LoadingSpinner size="lg" />
            </div>
          ) : filteredStations.length === 0 ? (
            <div className="text-center py-20">
              <Building2 className="w-16 h-16 text-midnight-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-midnight-700">No stations found</h3>
              <p className="text-midnight-500 mt-2">Add a new station to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-midnight-50 border-b border-midnight-100">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-midnight-600">Station</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-midnight-600">Code</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-midnight-600">Location</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-midnight-600">Contact</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-midnight-600">Status</th>
                    <th className="text-right px-6 py-4 text-sm font-semibold text-midnight-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-midnight-100">
                  {filteredStations.map((station) => (
                    <tr key={station._id} className="hover:bg-midnight-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-semibold text-midnight-900">{station.name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-3 py-1 bg-railway-100 text-railway-700 rounded-lg font-mono font-bold">
                          {station.code}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-midnight-600">
                          <MapPin className="w-4 h-4" />
                          <span>{station.city}, {station.state}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {station.contact ? (
                          <div className="flex items-center gap-2 text-midnight-600">
                            <Phone className="w-4 h-4" />
                            <span>{station.contact}</span>
                          </div>
                        ) : (
                          <span className="text-midnight-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleToggleActive(station)}
                          className={`flex items-center gap-2 ${
                            station.isActive ? 'text-green-600' : 'text-midnight-400'
                          }`}
                        >
                          {station.isActive ? (
                            <>
                              <ToggleRight className="w-6 h-6" />
                              <span className="text-sm font-medium">Active</span>
                            </>
                          ) : (
                            <>
                              <ToggleLeft className="w-6 h-6" />
                              <span className="text-sm font-medium">Inactive</span>
                            </>
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleOpenModal(station)}
                            className="p-2 text-midnight-500 hover:text-railway-500 hover:bg-railway-50 rounded-lg transition-colors"
                            title="Edit Station"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleOpenDeleteModal(station)}
                            className="p-2 text-midnight-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete Station"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingStation ? 'Edit Station' : 'Add New Station'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-midnight-700 mb-2">
              Station Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., New Delhi Station"
              className="input-field"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-midnight-700 mb-2">
              Station Code *
            </label>
            <input
              type="text"
              name="code"
              value={formData.code}
              onChange={handleChange}
              placeholder="e.g., NDLS"
              className="input-field uppercase"
              maxLength={10}
              required
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-midnight-700 mb-2">
                City *
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="e.g., New Delhi"
                className="input-field"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-midnight-700 mb-2">
                State *
              </label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleChange}
                placeholder="e.g., Delhi"
                className="input-field"
                required
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-midnight-700 mb-2">
              Contact Number (Optional)
            </label>
            <input
              type="text"
              name="contact"
              value={formData.contact}
              onChange={handleChange}
              placeholder="e.g., 011-23747000"
              className="input-field"
            />
          </div>
          
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={handleCloseModal} className="btn-secondary flex-1">
              Cancel
            </button>
            <button type="submit" className="btn-primary flex-1">
              {editingStation ? 'Update Station' : 'Create Station'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        title="Delete Station"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-4 p-4 bg-red-50 rounded-xl">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h4 className="font-semibold text-red-900">Permanently Delete Station?</h4>
              <p className="text-sm text-red-700 mt-1">
                This will <strong>permanently delete</strong> the station <strong>"{deletingStation?.name}"</strong> ({deletingStation?.code}) and:
              </p>
              <ul className="text-sm text-red-700 mt-2 list-disc list-inside space-y-1">
                <li>All rooms at this station</li>
                <li>All room categories at this station</li>
                <li>Unassign all admins from this station</li>
              </ul>
              <p className="text-sm text-red-800 mt-2 font-medium">
                This action cannot be undone.
              </p>
            </div>
          </div>
          
          <div className="flex gap-3 pt-2">
            <button onClick={handleCloseDeleteModal} className="btn-secondary flex-1">
              Cancel
            </button>
            <button 
              onClick={handleDelete} 
              className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
            >
              Delete Permanently
            </button>
          </div>
        </div>
      </Modal>
    </AdminLayout>
  );
};

export default StationsManagement;
